import {
  LinkBreak1Icon,
  MagicWandIcon,
  MagnifyingGlassIcon,
  ResetIcon,
  TargetIcon,
  VideoIcon,
} from '@radix-ui/react-icons';
import { Badge, Button, IconButton, Text } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import {
  BaseballIcon,
  BusyMachineIcon,
  ConnectedMachineIcon,
  DisconnectedMachineIcon,
  LowerMachineIcon,
  R2FIcon,
  SuperAdminIcon,
} from 'components/common/custom-icon/shorthands';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonMenu } from 'components/common/menu';
import {
  ISidebarNavigation,
  SidebarNavigationTrigger,
} from 'components/main/sidebar/nav-trigger';
import env from 'config';
import { MachineContext, MachineDialogMode } from 'contexts/machine.context';
import { t } from 'i18next';
import { IMenuAction } from 'interfaces/i-menus';
import { DataCollectionType } from 'lib_ts/enums/data-collection.enums';
import { InstallStep } from 'lib_ts/enums/installation';
import { QueueState } from 'lib_ts/enums/machine-msg.enum';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IMachine } from 'lib_ts/interfaces/i-machine';
import { SpecialMsPosition } from 'lib_ts/interfaces/machine-msg/i-special-mstarget';
import { forwardRef, useContext, useMemo } from 'react';
import { useAuthStore } from 'stores/use-auth-store';
import useMachineStatusStore from 'stores/use-machine-status-store';

export enum ActiveConnectionActionGroup {
  // _ prefix indicates that we do not want to print the group name in the menu
  Controls = '_1',
  DataCollection = '_2',
  Diagnostics = '_3',
}

interface IProps {
  // whether the first item in the menu is the machineID and nickname
  machineInMenu?: boolean;

  // provide to show live view menu item
  onEnableLiveView?: () => void;

  // which action groups to show in the menu when active - defaults to all if not provided
  activeConnectionActionGroups?: ActiveConnectionActionGroup[];
  as: 'button' | 'icon' | 'nav';
}

interface ITriggerProps extends ISidebarNavigation {
  as: 'button' | 'icon' | 'nav';
}

// forwardRef needed to allow CommonMenu / DropdownMenu.Trigger to handle Trigger elements
const Trigger = forwardRef(
  // avoid spreading "small" prop to Button
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  ({ as, small, ...props }: ITriggerProps, ref: React.Ref<any>) => {
    switch (as) {
      case 'button': {
        return (
          <Button
            {...props}
            size={RADIX.BUTTON.SIZE.MODAL}
            variant="soft"
            ref={ref}
          >
            {props.icon}
            <Text color={props.color} truncate>
              {t(props.label)}
            </Text>
            {props.suffix}
          </Button>
        );
      }

      case 'icon': {
        return (
          <IconButton
            {...props}
            color={RADIX.COLOR.NEUTRAL}
            variant="soft"
            title={t(props.label).toString()}
            ref={ref}
          >
            {props.icon}
          </IconButton>
        );
      }

      default: {
        return <SidebarNavigationTrigger {...props} small={small} />;
      }
    }
  }
);

// TS will complain without a displayName
Trigger.displayName = 'Trigger';

const getConnectionLabel = (machine: IMachine): string => {
  if (machine.nickname) {
    return `${machine.machineID} (${machine.nickname})`;
  }

  return machine.machineID;
};

const ConnectionMachineDC = (props: IProps) => {
  const { machine, reconnect } = useContext(MachineContext);

  const label = useMemo(() => getConnectionLabel(machine), [machine]);

  return (
    <ErrorBoundary identifier="ConnectionMachineDC">
      <Trigger
        data-testid="ConnectionButton"
        data-status="machine-disconnected"
        data-machineid={machine.machineID}
        icon={<DisconnectedMachineIcon />}
        title={t('common.not-connected-to-x', {
          x: label,
        }).toString()}
        label={label}
        suffix={<Badge>{t('common.disconnected-shorthand')}</Badge>}
        color={RADIX.COLOR.DANGER}
        onClick={reconnect}
        as={props.as}
        small
      />
    </ErrorBoundary>
  );
};

const ConnectionUserDC = (props: IProps) => {
  const { machine, reconnect } = useContext(MachineContext);

  const label = useMemo(() => getConnectionLabel(machine), [machine]);

  return (
    <ErrorBoundary identifier="ConnectionUserDC">
      <Trigger
        data-testid="ConnectionButton"
        data-status="user-disconnected"
        data-machineid={machine.machineID}
        icon={<LinkBreak1Icon />}
        title={t('common.not-connected-to-x', {
          x: label,
        }).toString()}
        label={label}
        suffix={<Badge>{t('common.disconnected-shorthand')}</Badge>}
        color={RADIX.COLOR.WARNING}
        onClick={reconnect}
        as={props.as}
        small
      />
    </ErrorBoundary>
  );
};

const ConnectionBusy = (props: IProps) => {
  const { machine, openDialog } = useContext(MachineContext);

  const label = useMemo(() => getConnectionLabel(machine), [machine]);

  return (
    <ErrorBoundary identifier="ConnectionBusy">
      <Trigger
        data-testid="ConnectionButton"
        data-status="busy"
        data-machineid={machine.machineID}
        icon={<BusyMachineIcon />}
        label={label}
        color={RADIX.COLOR.WARNING}
        suffix={<Badge>{t('common.busy')}</Badge>}
        onClick={() => openDialog(MachineDialogMode.RequestControl)}
        as={props.as}
        small
      />
    </ErrorBoundary>
  );
};

const ConnectionActive = (props: IProps) => {
  const {
    // show all action groups by default
    activeConnectionActionGroups = [
      ActiveConnectionActionGroup.Controls,
      ActiveConnectionActionGroup.DataCollection,
      ActiveConnectionActionGroup.Diagnostics,
    ],
  } = props;

  const userRoleFeatureFlags = useAuthStore((s) => s.userRoleFeatureFlags);
  const current = useAuthStore((s) => s.current);
  const isInGame = useAuthStore((s) => s.isInGame);

  const {
    machine,
    openDialog: machineCxOpenDialog,
    specialMsTarget,
    update: updateMachine,
    createDataCollection,
  } = useContext(MachineContext);

  const label = useMemo(() => getConnectionLabel(machine), [machine]);

  const isInstalling =
    machine.last_installation && !machine.last_installation.end_date;

  const actions = useMemo((): IMenuAction[] => {
    const output: IMenuAction[] = [];

    if (
      activeConnectionActionGroups.includes(
        ActiveConnectionActionGroup.Controls
      )
    ) {
      output.push(
        {
          invisible: !props.machineInMenu,
          group: ActiveConnectionActionGroup.Controls,
          label: label,
          color: RADIX.COLOR.SUCCESS,
          className: 'ponter-events-none',
          onClick: () => {
            // do nothing
          },
        },
        {
          group: ActiveConnectionActionGroup.Controls,
          prefixIcon: <R2FIcon />,
          label: 'common.status',
          onClick: () => machineCxOpenDialog(MachineDialogMode.Status),
        },
        {
          // todo: when ready to release this feature, show it for every env
          invisible: env.production || !props.onEnableLiveView,
          group: ActiveConnectionActionGroup.Controls,
          prefixIcon: <VideoIcon />,
          label: 'main.live-view',
          onClick: () => props.onEnableLiveView?.(),
        },
        {
          group: ActiveConnectionActionGroup.Controls,
          prefixIcon: <LowerMachineIcon />,
          label: 'common.lower',
          onClick: () => specialMsTarget(SpecialMsPosition.home),
        },
        {
          group: ActiveConnectionActionGroup.Controls,
          prefixIcon: <MagnifyingGlassIcon />,
          label: 'main.inspect',
          onClick: () => machineCxOpenDialog(MachineDialogMode.Inspect),
        },
        {
          group: ActiveConnectionActionGroup.Controls,
          prefixIcon: <ResetIcon />,
          label: 'common.restart',
          color: RADIX.COLOR.WARNING,
          onClick: () => machineCxOpenDialog(MachineDialogMode.Restart),
        }
      );
    }

    if (
      activeConnectionActionGroups.includes(
        ActiveConnectionActionGroup.DataCollection
      )
    ) {
      output.push(
        {
          group: ActiveConnectionActionGroup.DataCollection,
          prefixIcon: <TargetIcon />,
          label: 'common.repeatability-test',
          suffixIcon: <SuperAdminIcon />,
          invisible: isInGame || !userRoleFeatureFlags.repeatability,
          onClick: async () => {
            // overwrite the machine last_installation and open the dialog
            if (!isInstalling) {
              const newDc = await createDataCollection(
                DataCollectionType.RepeatabilityTest,
                machine.machineID,
                machine.ball_type
              );

              if (!newDc) {
                return;
              }

              await updateMachine({
                _id: machine._id,
                last_installation: {
                  standalone: InstallStep.Repeatability,
                  started_by: current.email,
                  start_date: new Date().toISOString(),
                  ball_type: machine.ball_type,
                  ball_type_locked: true,
                  rtID: newDc._id,

                  btcID: null,
                  end_date: null,
                  finished_by: null,
                  model_id: null,

                  step_ball_type: null,
                  step_completion: null,
                  step_general_config: null,
                  step_hardware_changes: null,
                  step_projector_config: null,
                  step_repeatability: null,
                  step_test_shot: null,
                  step_vision: null,

                  vccID: null,
                  vcdFt: null,
                  vcdComplete: null,
                },
              });

              machineCxOpenDialog(MachineDialogMode.Installation);
              return;
            }

            // * install is in progress
            const { last_installation } = machine;

            if (!last_installation) {
              // shouldn't trigger but makes TS happy
              return;
            }

            // if standalone RT and for the current ball type, treat this as a resume
            if (
              last_installation.standalone === InstallStep.Repeatability &&
              last_installation.ball_type === machine.ball_type
            ) {
              machineCxOpenDialog(MachineDialogMode.Installation);
              return;
            }

            // show a toast instead
            NotifyHelper.warning({
              message_md: `Please complete or cancel the installation for ${last_installation.ball_type} before trying again.`,
            });
          },
        },
        {
          group: ActiveConnectionActionGroup.DataCollection,
          prefixIcon: <BaseballIcon />,
          label: 'common.ball-type-calibration',
          invisible: isInGame,
          onClick: async () => {
            // overwrite the machine last_installation and open the dialog
            if (!isInstalling) {
              const newDc = await createDataCollection(
                DataCollectionType.BallTypeCalibration,
                machine.machineID,
                machine.ball_type
              );

              if (!newDc) {
                return;
              }

              await updateMachine({
                _id: machine._id,
                last_installation: {
                  standalone: InstallStep.BallType,
                  started_by: current.email,
                  start_date: new Date().toISOString(),
                  ball_type: machine.ball_type,
                  ball_type_locked: true,
                  btcID: newDc._id,

                  end_date: null,
                  finished_by: null,
                  model_id: null,
                  rtID: null,

                  step_ball_type: null,
                  step_completion: null,
                  step_general_config: null,
                  step_hardware_changes: null,
                  step_projector_config: null,
                  step_repeatability: null,
                  step_test_shot: null,
                  step_vision: null,

                  vccID: null,
                  vcdFt: null,
                  vcdComplete: null,
                },
              });

              machineCxOpenDialog(MachineDialogMode.Installation);
              return;
            }

            // * install is in progress
            const { last_installation } = machine;

            if (!last_installation) {
              // shouldn't trigger but makes TS happy
              return;
            }

            // if standalone BTC and for the current ball type, treat this as a resume
            if (
              last_installation.standalone === InstallStep.BallType &&
              last_installation.ball_type === machine.ball_type
            ) {
              machineCxOpenDialog(MachineDialogMode.Installation);
              return;
            }

            // show a toast instead
            NotifyHelper.warning({
              message_md: `Please complete or cancel the installation for ${last_installation.ball_type} before trying again.`,
            });
          },
        }
      );
    }

    if (
      activeConnectionActionGroups.includes(
        ActiveConnectionActionGroup.Diagnostics
      )
    ) {
      output.push(
        {
          invisible: !userRoleFeatureFlags.healthCheck,
          group: ActiveConnectionActionGroup.Diagnostics,
          prefixIcon: <MagnifyingGlassIcon />,
          label: 'common.health-check',
          suffixIcon: <SuperAdminIcon />,
          onClick: () => machineCxOpenDialog(MachineDialogMode.HealthCheck),
        },
        {
          invisible: !userRoleFeatureFlags.machineDebugger,
          group: ActiveConnectionActionGroup.Diagnostics,
          prefixIcon: <MagicWandIcon />,
          label: 'common.debug',
          suffixIcon: <SuperAdminIcon />,
          onClick: () => machineCxOpenDialog(MachineDialogMode.Debug),
        }
      );
    }

    return output.filter((m) => !m.invisible);
  }, [
    props.machineInMenu,
    props.onEnableLiveView,
    machineCxOpenDialog,
    specialMsTarget,
    isInGame,
    createDataCollection,
    machine,
    isInstalling,
    activeConnectionActionGroups,
    userRoleFeatureFlags,
  ]);

  return (
    <ErrorBoundary identifier="ConnectionActive">
      <CommonMenu
        actions={actions}
        title={label}
        trigger={
          <Trigger
            data-testid="ConnectionButton"
            data-status="connected"
            data-machineid={machine.machineID}
            icon={<ConnectedMachineIcon />}
            label={label}
            color={RADIX.COLOR.SUCCESS}
            actions={actions}
            as={props.as}
            small
          />
        }
        skipSort
      />
    </ErrorBoundary>
  );
};

export const MachineConnectionTrigger = (props: IProps) => {
  const queueState = useMachineStatusStore((s) => s.queueState);

  switch (queueState) {
    case QueueState.Active: {
      return <ConnectionActive {...props} />;
    }

    case QueueState.Busy: {
      return <ConnectionBusy {...props} />;
    }

    case QueueState.MachineDC: {
      return <ConnectionMachineDC {...props} />;
    }

    case QueueState.UserDC: {
      return <ConnectionUserDC {...props} />;
    }

    default: {
      return null;
    }
  }
};
